import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import { ComponentJsonLogicKey } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/types';
import { ComponentLibrarySupportedType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import {
  ComponentLibrarySection,
  ScreenEditorComponent,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import ButtonComponent from 'images/dashboard/screen-editor-components/component-button.svg';
import ClickableContainerComponent from 'images/dashboard/screen-editor-components/component-clickable-container.svg';
import ContainerComponent from 'images/dashboard/screen-editor-components/component-container.svg';
import FooterComponent from 'images/dashboard/screen-editor-components/component-footer.svg';
import ImageComponent from 'images/dashboard/screen-editor-components/component-image.svg';
import InputTextComponent from 'images/dashboard/screen-editor-components/component-input.svg';
import InputAddressComponent from 'images/dashboard/screen-editor-components/component-input-address.svg';
import InputTextAreaComponent from 'images/dashboard/screen-editor-components/component-input-area.svg';
import InputCheckboxComponent from 'images/dashboard/screen-editor-components/component-input-checkbox.svg';
import InputCheckboxGroupComponent from 'images/dashboard/screen-editor-components/component-input-checkbox-group.svg';
import InputDateComponent from 'images/dashboard/screen-editor-components/component-input-date.svg';
import InputMaskedTextComponent from 'images/dashboard/screen-editor-components/component-input-masked-text.svg';
import InputNumberComponent from 'images/dashboard/screen-editor-components/component-input-number.svg';
import InputPhoneComponent from 'images/dashboard/screen-editor-components/component-input-phone.svg';
import InputSelectComponent from 'images/dashboard/screen-editor-components/component-input-select.svg';
import InputMultiSelectComponent from 'images/dashboard/screen-editor-components/component-multi-select.svg';
import InputRadioGroupComponent from 'images/dashboard/screen-editor-components/component-radio-group.svg';
import SpacerComponent from 'images/dashboard/screen-editor-components/component-spacer.svg';
import StepperComponent from 'images/dashboard/screen-editor-components/component-stepper.svg';
import TextComponent from 'images/dashboard/screen-editor-components/component-text.svg';
import TitleComponent from 'images/dashboard/screen-editor-components/component-title.svg';
import { UiComponentConfigType } from 'lib/interfaces/ui-components';

export const COMPONENT_JSONLOGIC_KEYS: Array<ComponentJsonLogicKey> = [
  'hidden',
  'hidden-server-evaluated',
  'disabled',
  'disabled-server-evaluated',
];

export const NONDELETABLE_COMPONENTS = [
  UiComponentConfigType.InputCheckboxDatetime, // used like PrivacyPolicy
  UiComponentConfigType.ButtonVerifyWithPersona,
  UiComponentConfigType.LanguageSelector,
  UiComponentConfigType.PersonaBranding,
  UiComponentConfigType.PrivacyPolicy,
];

export const NONEDITABLE_COMPONENTS = [
  UiComponentConfigType.FailureReasonImage,
  UiComponentConfigType.InputConfirmationCode,
  UiComponentConfigType.QrCode,
  UiComponentConfigType.TableStatic,
  UiComponentConfigType.PersonaBranding,
  UiComponentConfigType.PrivacyPolicy,
];

export const NON_WEB_PREVIEW_COMPONENTS = [
  UiComponentConfigType.ButtonDeviceHandoff,
  UiComponentConfigType.CombinedStepButton,
  UiComponentConfigType.CombinedStepImagePreview,
];

// Components that cannot be deleted, edited, or previewed in FLE
export const RESTRICTED_COMPONENTS = NONDELETABLE_COMPONENTS.concat(
  ...NONEDITABLE_COMPONENTS,
  ...NON_WEB_PREVIEW_COMPONENTS,
);

// Components that have no directly editable config attributes
export const COMPONENTS_WITHOUT_PROPERTY_SUPPORT = [UiComponentConfigType.Footer];

// Components that have no directly editable config attributes other than JSON logic fields.
// These should only render the properties panel when logic editing is enabled in screen editor
export const COMPONENTS_WITH_ONLY_JSON_LOGIC = [
  UiComponentConfigType.HorizontalStack,
  UiComponentConfigType.Spacer,
  UiComponentConfigType.FailureReasonText,
];

export const NONDRAGGABLE_COMPONENTS = [UiComponentConfigType.Footer];

// Components that derive their styles from the `config` attribute when
// the feature flag `inquiry:use-component-styles` is off
export const CONFIG_STYLE_COMPONENTS = [
  UiComponentConfigType.Spacer,
  UiComponentConfigType.ImageHosted,
];

// Components that support style updates within screen editor
export const STYLEABLE_COMPONENTS = [
  UiComponentConfigType.Spacer,
  UiComponentConfigType.Text,
  UiComponentConfigType.Title,
  UiComponentConfigType.ImageLocal,
  UiComponentConfigType.ImageHosted,
  UiComponentConfigType.ClickableStack,
  UiComponentConfigType.HorizontalStack,
  UiComponentConfigType.Footer,
  UiComponentConfigType.ButtonAction,
  UiComponentConfigType.ButtonCancel,
  UiComponentConfigType.ButtonComplete,
  UiComponentConfigType.ButtonSubmit,
  UiComponentConfigType.InputCheckbox,
  UiComponentConfigType.InputCheckboxDatetime,
  UiComponentConfigType.FailureReasonText,
  UiComponentConfigType.Stepper,
];

export const ComponentAccessTiers: Partial<Record<UiComponentConfigType, EditorAccessTier>> = {
  [UiComponentConfigType.ButtonAction]: EditorAccessTier.InquiriesBasic,
  [UiComponentConfigType.ButtonCancel]: EditorAccessTier.InquiriesBasic,
  [UiComponentConfigType.ButtonComplete]: EditorAccessTier.InquiriesBasic,
  [UiComponentConfigType.ButtonDeviceHandoff]: EditorAccessTier.InquiriesBasic,
  [UiComponentConfigType.ButtonSubmit]: EditorAccessTier.InquiriesBasic,
  [UiComponentConfigType.Footer]: EditorAccessTier.InquiriesBasic,
  [UiComponentConfigType.ImageLocal]: EditorAccessTier.InquiriesFree,
  [UiComponentConfigType.ImageHosted]: EditorAccessTier.InquiriesFree,
  [UiComponentConfigType.FailureReasonText]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.FailureReasonImage]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.LanguageSelector]: EditorAccessTier.Any,
  [UiComponentConfigType.PersonaBranding]: EditorAccessTier.Any,
  [UiComponentConfigType.PrivacyPolicy]: EditorAccessTier.Any,
  [UiComponentConfigType.Spacer]: EditorAccessTier.InquiriesFree,
  [UiComponentConfigType.Text]: EditorAccessTier.InquiriesFree,
  [UiComponentConfigType.Title]: EditorAccessTier.InquiriesFree,
  [UiComponentConfigType.ClickableStack]: EditorAccessTier.InquiriesBasic,
  [UiComponentConfigType.HorizontalStack]: EditorAccessTier.InquiriesBasic,
  [UiComponentConfigType.InputAddress]: EditorAccessTier.InquiriesBasic,
  [UiComponentConfigType.InputCheckbox]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.InputCheckboxDatetime]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.InputCheckboxGroup]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.InputConfirmationCode]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.InputCountrySelect]: EditorAccessTier.InquiriesBasic,
  [UiComponentConfigType.InputCurrency]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.InputDate]: EditorAccessTier.InquiriesBasic,
  [UiComponentConfigType.InputMaskedText]: EditorAccessTier.InquiriesBasic,
  [UiComponentConfigType.InputMultiSelect]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.InputNumber]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.InputPhoneNumber]: EditorAccessTier.InquiriesBasic,
  [UiComponentConfigType.InputRadioGroup]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.InputSelect]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.InputText]: EditorAccessTier.InquiriesFree,
  [UiComponentConfigType.InputTextArea]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.InputFileUpload]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.InputInternationalDb]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.InputSubdivisionSelect]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.InputUrl]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.QrCode]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.TableStatic]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.Stepper]: EditorAccessTier.InquiriesEnhanced,
  [UiComponentConfigType.GovernmentIdTypeSelect]: EditorAccessTier.InquiriesEnhanced,
};

/**
 * Note(Evan): To render draggable components within the component library panel,
 * you need to add the componentLibrary attribute:
 *
 * ```
   [COMPONENT_CONFIG_TYPE]: {
    displayName: 'Foo'
    componentLibrary: {
      icon: <COMPONENT_ICON />
      section: ComponentLibrarySection
    }
   }
 ```
 * */
const _ScreenEditorComponentConfigTypes: Partial<
  Record<UiComponentConfigType, ScreenEditorComponent>
> = {
  [UiComponentConfigType.ButtonAction]: {
    displayName: 'Button',
  },
  [UiComponentConfigType.ButtonCancel]: {
    displayName: 'Button',
  },
  [UiComponentConfigType.ButtonComplete]: {
    displayName: 'Button',
  },
  [UiComponentConfigType.ButtonDeviceHandoff]: {
    displayName: 'Button',
  },
  [UiComponentConfigType.ButtonSubmit]: {
    displayName: 'Button',
    componentLibrary: {
      icon: <ButtonComponent />,
      section: ComponentLibrarySection.BASIC,
    },
  },
  [UiComponentConfigType.ButtonVerifyWithPersona]: {
    displayName: 'Verify With Persona Button',
  },
  [UiComponentConfigType.Footer]: {
    displayName: 'Footer',
    componentLibrary: {
      icon: <FooterComponent />,
      section: ComponentLibrarySection.BASIC,
    },
  },
  [UiComponentConfigType.GovernmentIdNfcScan]: {
    displayName: 'Government ID NFC Scan',
  },
  [UiComponentConfigType.ImageLocal]: {
    displayName: 'Image',
    componentLibrary: {
      icon: <ImageComponent />,
      section: ComponentLibrarySection.BASIC,
    },
  },
  [UiComponentConfigType.ImageHosted]: {
    displayName: 'Image',
  },
  [UiComponentConfigType.FailureReasonText]: {
    displayName: 'Failure text',
  },
  [UiComponentConfigType.FailureReasonImage]: {
    displayName: 'Failed icon',
  },
  [UiComponentConfigType.LanguageSelector]: {
    displayName: 'Language selector',
  },
  [UiComponentConfigType.PersonaBranding]: {
    displayName: 'Persona branding',
  },
  [UiComponentConfigType.PrivacyPolicy]: {
    displayName: 'Privacy policy',
  },
  [UiComponentConfigType.Spacer]: {
    displayName: 'Spacer',
    componentLibrary: {
      icon: <SpacerComponent />,
      section: ComponentLibrarySection.BASIC,
    },
  },
  [UiComponentConfigType.Text]: {
    displayName: 'Text',
    componentLibrary: {
      icon: <TextComponent />,
      section: ComponentLibrarySection.BASIC,
    },
  },
  [UiComponentConfigType.Title]: {
    displayName: 'Title',
    componentLibrary: {
      icon: <TitleComponent />,
      section: ComponentLibrarySection.BASIC,
    },
  },
  [UiComponentConfigType.ClickableStack]: {
    displayName: 'Clickable container',
    componentLibrary: {
      icon: <ClickableContainerComponent />,
      section: ComponentLibrarySection.BASIC,
      hideUiComponentDragPreview: true,
    },
  },
  [UiComponentConfigType.Stepper]: {
    displayName: 'Stepper',
    featureFlag: {
      flag: 'dashboard:ite-enable-stepper-config-editor',
      renderValue: true,
    },
    componentLibrary: {
      icon: <StepperComponent />,
      section: ComponentLibrarySection.BASIC,
    },
  },
  [UiComponentConfigType.HorizontalStack]: {
    displayName: 'Container',
    componentLibrary: {
      icon: <ContainerComponent />,
      section: ComponentLibrarySection.BASIC,
      hideUiComponentDragPreview: true,
    },
  },

  [UiComponentConfigType.InputAddress]: {
    displayName: 'Address input',
    componentLibrary: {
      icon: <InputAddressComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputCheckbox]: {
    displayName: 'Checkbox',
    componentLibrary: {
      icon: <InputCheckboxComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputCheckboxDatetime]: {
    displayName: 'Consent checkbox',
    // No self serve
  },
  [UiComponentConfigType.InputCheckboxGroup]: {
    displayName: 'Checkbox group',
    componentLibrary: {
      icon: <InputCheckboxGroupComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputConfirmationCode]: {
    displayName: 'Confirmation code',
  },
  [UiComponentConfigType.InputCountrySelect]: {
    displayName: 'Country select',
    componentLibrary: {
      icon: <InputSelectComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputCurrency]: {
    displayName: 'Currency input',
  },
  [UiComponentConfigType.InputDate]: {
    displayName: 'Date',
    componentLibrary: {
      icon: <InputDateComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputMaskedText]: {
    displayName: 'Masked text',
    componentLibrary: {
      icon: <InputMaskedTextComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputMultiSelect]: {
    displayName: 'Multi select',
    componentLibrary: {
      icon: <InputMultiSelectComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputNumber]: {
    displayName: 'Number input',
    componentLibrary: {
      icon: <InputNumberComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputPhoneNumber]: {
    displayName: 'Phone number',
    componentLibrary: {
      icon: <InputPhoneComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputRadioGroup]: {
    displayName: 'Radio group',
    componentLibrary: {
      icon: <InputRadioGroupComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputSelect]: {
    displayName: 'Select',
    componentLibrary: {
      icon: <InputSelectComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputText]: {
    displayName: 'Text input',
    componentLibrary: {
      icon: <InputTextComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputTextArea]: {
    displayName: 'Text area',
    componentLibrary: {
      icon: <InputTextAreaComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputUrl]: {
    displayName: 'URL input',
    componentLibrary: {
      icon: <InputTextComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputSubdivisionSelect]: {
    displayName: 'Subdivision select',
  },
  [UiComponentConfigType.QrCode]: {
    displayName: 'QR code',
  },
  [UiComponentConfigType.TableStatic]: {
    displayName: 'Table',
  },
  [UiComponentConfigType.InputESignature]: {
    displayName: 'Electronic signature input',
  },
  [UiComponentConfigType.InputFileUpload]: {
    displayName: 'File upload',
    featureFlag: {
      flag: 'dashboard:fle-enable-self-serve-file-upload-component',
      renderValue: true,
    },
    componentLibrary: {
      icon: <InputTextComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.InputInternationalDb]: {
    displayName: 'International database inputs',
    featureFlag: {
      flag: 'dashboard:fle-enable-self-serve-international-db-component',
      renderValue: true,
    },
    componentLibrary: {
      icon: <InputMaskedTextComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
  [UiComponentConfigType.GovernmentIdTypeSelect]: {
    displayName: 'Government ID Type Select',
    componentLibrary: {
      icon: <ClickableContainerComponent />,
      section: ComponentLibrarySection.FORMS,
    },
  },
};

type ScreenEditorComponentTuple = [ComponentLibrarySupportedType, ScreenEditorComponent];

const sortComponentsByDisplayName = (a: UiComponentConfigType, b: UiComponentConfigType) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return _ScreenEditorComponentConfigTypes[a].displayName >
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    _ScreenEditorComponentConfigTypes[b].displayName
    ? 1
    : -1;
};

export const ScreenEditorComponentConfigTypes = Object.fromEntries(
  Object.entries(_ScreenEditorComponentConfigTypes).sort(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (a: ScreenEditorComponentTuple, b: ScreenEditorComponentTuple) =>
      sortComponentsByDisplayName(a[0], b[0]),
  ),
);
